import React, { useState } from 'react'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Label } from '@/components/ui/label.jsx'
import { Textarea } from '@/components/ui/textarea.jsx'
import { X, CheckCircle, AlertCircle } from 'lucide-react'

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitStatus('success')
        setTimeout(() => {
          onClose()
          setSubmitStatus(null)
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          })
        }, 2000)
      } else {
        setSubmitStatus('error')
        console.error('Failed to send email:', result.error)
      }
    } catch (error) {
      setSubmitStatus('error')
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Contact Us</CardTitle>
              <CardDescription>Get in touch with our team</CardDescription>
            </div>
            <button
              onClick={onClose}
              className="text-gray-900 hover:text-primary hover:bg-gray-100 p-2 rounded-lg transition-colors flex-shrink-0"
              aria-label="Close"
              type="button"
            >
              <X className="h-6 w-6" style={{ color: '#000000', strokeWidth: 2 }} />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-4" style={{ color: '#962831' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#962831' }}>Message Sent!</h3>
              <p className="text-gray-600">We'll get back to you soon.</p>
            </div>
          ) : submitStatus === 'error' ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">Failed to Send</h3>
              <p className="text-gray-600 mb-4">There was an error sending your message. Please try again.</p>
              <Button onClick={() => setSubmitStatus(null)} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact-name" className="mb-2 block text-gray-900">Full Name *</Label>
                <Input
                  id="contact-name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="contact-email" className="mb-2 block text-gray-900">Email *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="contact-subject" className="mb-2 block text-gray-900">Subject</Label>
                <Input
                  id="contact-subject"
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <Label htmlFor="contact-message" className="mb-2 block text-gray-900">Message *</Label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Your message..."
                  rows={4}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-white hover:bg-primary/90 hover:text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default ContactForm

